import { useEffect, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';
import { AiCoach_AsyncTaskStatus as AsyncTaskStatus } from '__generated__/graphql-types';

import type {
  AiCoachPlatform_CoachAsyncTaskQuery as CoachAsyncTaskQuery,
  AiCoachPlatform_CoachAsyncTaskQueryVariables as CoachAsyncTaskQueryVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachAsyncTask';
import aiCoachAsyncTaskQuery from 'bundles/ai-coach-platform/queries/aiCoachAsyncTask.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';

const DEFAULT_POLL_INTERVAL_MS = 10_000;
const MAX_POLL_COUNT = 10;

type Props = {
  taskId?: string;
  pollInterval?: number;
  maxPollCount?: number;
};

const useCoachAsyncTask = ({
  taskId = '',
  pollInterval = DEFAULT_POLL_INTERVAL_MS,
  maxPollCount = MAX_POLL_COUNT,
}: Props): {
  taskStatus: AsyncTaskStatus | undefined;
  loading: boolean;
  error?: ApolloError;
  fetchTaskStatus: (taskIdToFetch: string) => Promise<AsyncTaskStatus | undefined>;
  stopPolling: () => void;
  onComplete?: (status: AsyncTaskStatus) => void;
} => {
  const [taskStatus, setTaskStatus] = useState<AsyncTaskStatus | undefined>(undefined);
  const [pollCount, setPollCount] = useState<number>(0);

  const [fetchQuery, { loading, stopPolling, error }] = useLazyQuery<CoachAsyncTaskQuery, CoachAsyncTaskQueryVariables>(
    aiCoachAsyncTaskQuery,
    {
      variables: {
        taskId,
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval,
      onCompleted: ({ coachAsyncTask }) => {
        const status = coachAsyncTask?.queryAsyncTaskStatus;

        if (status) {
          setTaskStatus(status);

          if (status === AsyncTaskStatus.Completed) {
            stopPolling();
            setPollCount(0);
          } else {
            // track poll count so we can stop at maxPollCount and avoid overloading BE
            setPollCount((prev) => prev + 1);
          }
        }
      },
    }
  );

  useEffect(() => {
    if (error) {
      captureMessageInSentry({ source: 'useCoachAsyncTask', error });
    }
  }, [error, loading]);

  useEffect(() => {
    if (pollCount >= maxPollCount) {
      stopPolling();
      setPollCount(0);
    }
  }, [pollCount, maxPollCount, stopPolling]);

  const handleFetchTaskStatus = async (taskIdToFetch: string) => {
    if (taskIdToFetch) {
      const { data } = await fetchQuery({ variables: { taskId: taskIdToFetch } });

      if (data) {
        const status = data.coachAsyncTask?.queryAsyncTaskStatus;

        setTaskStatus(status);
        return status;
      }
    }

    return undefined;
  };

  return {
    taskStatus,
    loading,
    error,
    fetchTaskStatus: handleFetchTaskStatus,
    stopPolling,
  };
};

export default useCoachAsyncTask;
